import { useState, useEffect, useRef } from "react";
import { C } from "../constants/theme";
import { fbList, fbWatch, fbUpdate, fbBatchUpdate } from "../hooks/useFirebase";
import { normalizarStatus } from "../constants/statusPedido";
import { Card } from "./ui/Card";
import "./operacao.css";

const STATUS_ATIVO = ["aguardando_impressao", "imprimindo"];

function fmtTs(ts) {
  if (!ts) return "";
  let d;
  if (ts.toDate) d = ts.toDate();
  else if (ts.seconds) d = new Date(ts.seconds * 1000);
  else d = new Date(ts);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function tsMs(ts) {
  if (!ts) return 0;
  if (ts.toDate) return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return new Date(ts).getTime();
}

// Foto do pedido vinculado a um item da fila (primeiro item do catálogo, se houver)
function fotoDoItem(item, pedidosAll, produtos) {
  const p = pedidosAll.find(x => x.id === item.pedidoId);
  if (!p) return null;
  const primeiro = (p.itens || [])[0] || { produto_id: p.produto_id };
  return produtos.find(pr => pr.id === primeiro.produto_id)?.fotoBase64 || null;
}

// Nome do produto + cliente do pedido vinculado, pra saber o que está marcando como urgente
function infoDoItem(item, pedidosAll) {
  const p = pedidosAll.find(x => x.id === item.pedidoId);
  if (!p) return { nome: item.nome || "Item avulso", cliente: null };
  return { nome: p.produto || "Pedido", cliente: p.cliente || null };
}

export function FilaImpressao() {
  const [fila, setFila]             = useState([]);
  const [pedidosAll, setPedidosAll] = useState([]);
  const [produtos, setProdutos]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const ordemEstavelRef              = useRef(new Map());
  const horariosRef                   = useRef(new Map());

  useEffect(() => {
    (async () => {
      const [f, p, pr] = await Promise.all([
        fbList("fila_impressao").catch(() => []),
        fbList("pedidos").catch(() => []),
        fbList("produtos").catch(() => []),
      ]);
      setFila(f); setPedidosAll(p); setProdutos(pr);
      setLoading(false);
    })();
    // O listener em tempo real serve do cache local primeiro (rápido mesmo
    // com a rede instável) — não espera só a busca avulsa acima pra sair do
    // "Carregando...", porque essa busca (getDocs simples) pode travar
    // vários segundos numa reconexão do Firestore e prender a tela zerada
    // à toa enquanto o listener já teria os dados certos.
    const unsubs = [
      fbWatch("fila_impressao", d => { setFila(d); setLoading(false); }),
      fbWatch("pedidos", setPedidosAll),
      fbWatch("produtos", setProdutos),
    ];
    return () => unsubs.forEach(u => u && u());
  }, []);

  // Só o que ainda está esperando ou imprimindo — sai da fila sozinho quando
  // o pedido avança (concluído/entregue/cancelado), via useFilaSync no App.js.
  // Dedup por pedidoId (mantém o mais antigo) + confere o status ATUAL do pedido
  // ligado, pra nunca mostrar duplicado ou um pedido já cancelado/concluído
  // caso a sincronização ainda não tenha alcançado esse item.
  const brutos = fila.filter(x => x.status === "aguardando" || x.status === "imprimindo");
  const porPedidoId = new Map();
  const avulsos = [];
  for (const item of brutos) {
    if (!item.pedidoId) { avulsos.push(item); continue; }
    const atual = porPedidoId.get(item.pedidoId);
    if (!atual || tsMs(item.created_at) < tsMs(atual.created_at)) porPedidoId.set(item.pedidoId, item);
  }
  // Posição na fila: usa o campo "ordem" quando existe (setinhas/urgente escrevem
  // nele); item que nunca foi mexido cai na hora de chegada — mantém o
  // comportamento antigo pra quem nunca usou os controles novos.
  // O serverTimestamp chega depois da primeira atualização do Firestore. A
  // referência conserva a primeira posição vista para o item e evita que ele
  // pule de lugar quando a data definitiva for recebida.
  const chave = (item) => {
    if (item.ordem != null) return Number(item.ordem);
    const salva = ordemEstavelRef.current.get(item.id);
    if (salva != null) return salva;
    const ordem = tsMs(item.created_at) || Date.now() + ordemEstavelRef.current.size;
    ordemEstavelRef.current.set(item.id, ordem);
    return ordem;
  };
  const horarioDoItem = (item) => {
    // Usa o pedido como chave quando ele existe: mesmo que um documento da
    // fila seja recebido novamente pelo snapshot, o horário não pisca.
    const id = item.pedidoId || item.id;
    const horario = fmtTs(item.created_at);
    if (horario) horariosRef.current.set(id, horario);
    return horario || horariosRef.current.get(id) || "Registrando…";
  };

  const pendentes = [...porPedidoId.values(), ...avulsos]
    .filter(item => {
      if (!item.pedidoId) return true;
      const p = pedidosAll.find(pp => pp.id === item.pedidoId);
      return p ? STATUS_ATIVO.includes(normalizarStatus(p.status)) : false;
    })
    .sort((a, b) => chave(a) - chave(b));
  const emProducao = pendentes.filter(item => {
    const pedido = pedidosAll.find(p => p.id === item.pedidoId);
    return item.status === "imprimindo" || normalizarStatus(pedido?.status) === "imprimindo";
  }).length;
  const urgentes = pendentes.filter(item => item.urgente).length;
  const aguardando = pendentes.length - emProducao;

  const toggleUrgente = async (item) => {
    try {
      if (item.urgente) {
        await fbUpdate("fila_impressao", item.id, { urgente: false, ordem: chave(item) });
      } else {
        const menor = Math.min(...pendentes.map(chave));
        await fbUpdate("fila_impressao", item.id, { urgente: true, ordem: menor - 1 });
      }
    } catch (e) { console.error("Erro ao marcar urgência:", e); }
  };

  // Troca a posição do item com o vizinho de cima/baixo na fila exibida.
  const mover = async (index, direcao) => {
    const alvo = index + direcao;
    if (alvo < 0 || alvo >= pendentes.length) return;
    const atual = pendentes[index];
    const vizinho = pendentes[alvo];
    const c1 = chave(atual), c2 = chave(vizinho);
    // Se os dois já tiverem a mesma "ordem" (empate — pode acontecer com itens
    // antigos nunca reordenados), só trocar os valores não muda nada. Nesse
    // caso desloca um pouco pro lado certo pra garantir uma troca de verdade.
    const [novoAtual, novoVizinho] = c1 === c2
      ? (direcao > 0 ? [c2 + 0.5, c1 - 0.5] : [c2 - 0.5, c1 + 0.5])
      : [c2, c1];
    try {
      // Batch atômico — as duas trocas caem juntas, sem passar por um estado
      // intermediário onde só uma delas já foi aplicada (o que bagunçava a
      // ordem quando cliques saíam em sequência rápida).
      await fbBatchUpdate([
        { col: "fila_impressao", id: atual.id,   data: { ordem: novoAtual } },
        { col: "fila_impressao", id: vizinho.id, data: { ordem: novoVizinho } },
      ]);
    } catch (e) { console.error("Erro ao reordenar fila:", e); }
  };

  return (
    <section className="print-queue-page" data-tour="fila-lista">
      <header className="operation-header">
        <div><span>OPERAÇÃO</span><h1>Fila de impressão</h1><p>Veja o que está em produção e ajuste a ordem dos próximos trabalhos.</p></div>
        <div className="queue-live-status"><i /> Atualizada em tempo real</div>
      </header>
      <section className="queue-overview" aria-label="Resumo da fila">
        <article><span>Na fila</span><strong>{pendentes.length}</strong><small>trabalhos ativos</small></article>
        <article className="is-printing"><span>Em produção</span><strong>{emProducao}</strong><small>rodando agora</small></article>
        <article className={urgentes ? "is-priority" : ""}><span>Prioridade</span><strong>{urgentes}</strong><small>{urgentes ? "aguardando atenção" : "nenhuma pendência"}</small></article>
        <article><span>Aguardando</span><strong>{aguardando}</strong><small>próximos a produzir</small></article>
      </section>
      <Card className="queue-list-card">
        <div className="queue-section-head"><div><span>ORDEM DE PRODUÇÃO</span><h2>Trabalhos ativos</h2></div><p>Use as setas para mudar a sequência.</p></div>
        <div style={{ fontSize: 12, color: "#7b8fa6", fontWeight: 700, letterSpacing: 1, marginBottom: 14 }}>🖨️ FILA DE IMPRESSÃO</div>
        {loading ? (
          <div style={{ color: C.muted, textAlign: "center", padding: 20 }}>⏳ Carregando...</div>
        ) : pendentes.length === 0 ? (
          <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "20px 0" }}>Fila vazia</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {pendentes.map((item, index) => {
              const foto = fotoDoItem(item, pedidosAll, produtos);
              const info = infoDoItem(item, pedidosAll);
              const arrowStyle = (habilitado) => ({
                background: "none", border: "1px solid " + C.border, borderRadius: 6, color: habilitado ? C.text : C.border,
                cursor: habilitado ? "pointer" : "default", fontSize: 11, lineHeight: 1, padding: "3px 6px", opacity: habilitado ? 1 : 0.4,
              });
              return (
                <div key={item.id} className={`print-job${item.urgente ? " is-urgent" : ""}${item.status === "imprimindo" ? " is-active" : ""}`}>
                  <div className="print-order-controls">
                    <button onClick={() => mover(index, -1)} disabled={index === 0} title="Subir na fila" style={arrowStyle(index !== 0)}>▲</button>
                    <button onClick={() => mover(index, 1)} disabled={index === pendentes.length - 1} title="Descer na fila" style={arrowStyle(index !== pendentes.length - 1)}>▼</button>
                  </div>
                  {foto ? (
                    <img src={foto} alt="" style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 44, height: 44, borderRadius: 8, background: C.panel, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>📦</div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.urgente && "🔥 "}{info.nome}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {info.cliente && <span style={{ color: C.muted, fontSize: 11 }}>👤 {info.cliente}</span>}
                      <span style={{ color: C.muted, fontSize: 11 }}>🕒 {horarioDoItem(item)}</span>
                    </div>
                  </div>
                  <button onClick={() => toggleUrgente(item)}
                    title={item.urgente ? "Remover urgência" : "Marcar como urgente — passa pro topo da fila"}
                    style={{ width: 30, height: 30, background: item.urgente ? "#ef4444" : "transparent", border: "1px solid #ef4444", color: item.urgente ? "#fff" : "#ef4444", borderRadius: 8, fontSize: 15, fontWeight: 900, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                    ❗
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </section>
  );
}
